(() => {
  const FILTER_PENDING_KEY = 'av_filter_apply_pending';

  const emit = (eventName, root, payload = {}) => {
    if (!window.AVAnalytics || typeof window.AVAnalytics.legacy !== 'function') return false;
    return window.AVAnalytics.legacy(eventName, {
      source: 'collection',
      list_context:
        root?.dataset.collectionHandle ||
        root?.closest('[data-collection-handle]')?.dataset.collectionHandle ||
        window.location.pathname,
      ...payload
    });
  };

  const normalizeFacet = (facet) => String(facet || '').replace(/^filter\.p\.m\./, '');

  const rememberPendingFilters = (form) => {
    try {
      const controls = [...form.elements]
        .filter((control) => {
          if (!control.name || control.disabled || control.name === 'sort_by') return false;
          if (control.type === 'checkbox') return control.checked;
          return control.value?.trim() !== '';
        })
        .map((control) => ({ facet: normalizeFacet(control.name), value: control.value }));

      if (controls.length === 0) {
        sessionStorage.removeItem(FILTER_PENDING_KEY);
        return;
      }

      sessionStorage.setItem(FILTER_PENDING_KEY, JSON.stringify({
        path: new URL(form.action, window.location.href).pathname,
        created_at: Date.now(),
        controls
      }));
    } catch (_) {
      // Filtering remains a native GET flow when storage is unavailable.
    }
  };

  const emitPendingFilters = (root, attempt = 0) => {
    try {
      const pending = JSON.parse(sessionStorage.getItem(FILTER_PENDING_KEY) || 'null');
      if (!pending || pending.path !== window.location.pathname || Date.now() - pending.created_at > 30000) {
        sessionStorage.removeItem(FILTER_PENDING_KEY);
        return;
      }

      const privacy = window.Shopify?.customerPrivacy;
      const analyticsReady = typeof window.AVAnalytics?.legacy === 'function';
      const privacyReady = typeof privacy?.analyticsProcessingAllowed === 'function';
      const analyticsAllowed = privacyReady && privacy.analyticsProcessingAllowed() === true;
      if (!analyticsReady || !privacyReady || !analyticsAllowed) {
        if (attempt < 7) {
          window.setTimeout(() => emitPendingFilters(root, attempt + 1), Math.min(1000, 100 * (attempt + 1)));
        } else {
          sessionStorage.removeItem(FILTER_PENDING_KEY);
        }
        return;
      }

      const results = Number(root.querySelector('[data-avc14-result-count]')?.dataset.avc14ResultCount || 0);
      const remaining = pending.controls.filter(({ facet, value }) => {
        return !emit('av_filter_apply', root, { facet, value, results });
      });
      if (remaining.length === 0) {
        sessionStorage.removeItem(FILTER_PENDING_KEY);
      } else if (attempt < 7) {
        sessionStorage.setItem(FILTER_PENDING_KEY, JSON.stringify({ ...pending, controls: remaining }));
        window.setTimeout(() => emitPendingFilters(root, attempt + 1), Math.min(1000, 100 * (attempt + 1)));
      } else {
        sessionStorage.removeItem(FILTER_PENDING_KEY);
      }
    } catch (_) {
      // Analytics must never interrupt collection browsing.
    }
  };

  const initCollection = (root) => {
    if (!root || root.dataset.avc14Ready === 'true') return;
    root.dataset.avc14Ready = 'true';

    const sort = root.querySelector('[data-avc14-sort]');
    const sortForm = root.querySelector('[data-avc14-sort-form]');
    const filterForm = root.querySelector('[data-avc14-filter-form]');
    const filterDisclosure = root.querySelector('[data-avc14-filter-disclosure]');

    sort?.addEventListener('change', () => sortForm?.requestSubmit());

    filterForm?.addEventListener('submit', () => {
      rememberPendingFilters(filterForm);
      const selectedInputs = [...filterForm.querySelectorAll('input[type="checkbox"]:checked')];
      const selectedLabels = selectedInputs
        .map(
          (input) =>
            input.closest('label')?.querySelector('[data-avc14-filter-value-label]')?.textContent?.trim() ||
            input.value
        )
        .filter(Boolean);
      const selectedGroups = [...new Set(selectedInputs.map((input) => input.name).filter(Boolean))];
      emit('australia:collection_filter_applied', root, {
        filter_group: selectedGroups.join('|') || 'price_range',
        filter_value: selectedLabels.join('|') || 'price_or_none'
      });
    });

    sortForm?.addEventListener('submit', () => {
      emit('australia:collection_filter_applied', root, {
        filter_group: 'sort_by',
        filter_value: sort?.value || 'default'
      });
    });

    filterDisclosure?.addEventListener('toggle', () => {
      if (filterDisclosure.open) emit('australia:collection_filter_opened', root);
    });

    root.querySelectorAll('.avc14-active-filters a').forEach((link) => {
      link.addEventListener('click', () => {
        if (link.classList.contains('avc14-active-filters__clear')) {
          emit('australia:collection_filter_reset', root, { action: 'clear_all' });
        } else {
          emit('australia:collection_filter_removed', root, { filter_value: link.textContent.trim() });
        }
      });
    });

    root.querySelector('.avc14-pagination__more')?.addEventListener('click', () => {
      emit('australia:collection_page_next', root);
    });

    const granolaPaths = [...root.querySelectorAll('[data-avc14-granola-path]')];
    const granolaChapters = [...root.querySelectorAll('[data-avc14-granola-chapter]')];
    const setActiveGranolaPath = (chapter) => {
      granolaPaths.forEach((path) => {
        if (path.dataset.avc14GranolaPath === chapter) {
          path.setAttribute('aria-current', 'true');
        } else {
          path.removeAttribute('aria-current');
        }
      });
    };

    if (granolaPaths.length === 3 && granolaChapters.length === 3) {
      granolaPaths.forEach((path) => {
        path.addEventListener('click', () => setActiveGranolaPath(path.dataset.avc14GranolaPath));
      });

      if ('IntersectionObserver' in window) {
        const chapterObserver = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (visible) setActiveGranolaPath(visible.target.dataset.avc14GranolaChapter);
          },
          { rootMargin: '-18% 0px -62% 0px', threshold: [0, 0.1, 0.25, 0.5] }
        );
        granolaChapters.forEach((chapter) => chapterObserver.observe(chapter));
      }
    }

    const serverEmpty = root.querySelector('.avc14-empty');
    if (serverEmpty && !serverEmpty.hidden) {
      emit('australia:collection_no_results', root, { result_count: 0 });
    }

    emitPendingFilters(root);
  };

  const init = (context = document) => {
    context.querySelectorAll('[data-avc14-collection-root]').forEach(initCollection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
